package dev.lingvo.ops.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.ops.api.OpsApiException
import dev.lingvo.ops.state.OpsResourceState
import dev.lingvo.shared.ops.OpsPage
import dev.lingvo.shared.ops.OpsWindow
import dev.lingvo.shared.ops.Untrusted
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The narrow measure used for explanatory operator copy. */
val OpsProseStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.BodySmall)
        .styleModifier {
            property("max-width", "66ch")
            property("line-height", TypeScale.BodyLineHeight.toString())
        }
}

val OpsPanelHeaderStyle = CssStyle.base {
    Modifier.width(100.percent).styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("flex-wrap", "wrap")
        property("gap", "12px 16px")
        property("min-width", "0")
    }
}

val OpsPageHeaderStyle = CssStyle.base {
    Modifier.width(100.percent).styleModifier {
        property("display", "flex")
        property("align-items", "flex-start")
        property("justify-content", "space-between")
        property("flex-wrap", "wrap")
        property("gap", "16px 24px")
        property("margin-bottom", "8px")
    }
}

/** One page title, a useful task description, and a refresh command with stable geometry. */
@Composable
fun OpsPageHeader(
    title: String,
    description: String,
    isRefreshing: Boolean = false,
    onRefresh: (() -> Unit)? = null,
) {
    Div(OpsPageHeaderStyle.toModifier().toAttrs()) {
        Div(Modifier.styleModifier { property("flex", "1 1 280px"); property("min-width", "0") }.toAttrs()) {
            H1(Modifier.margin(0.px).fontSize(TypeScale.Heading).fontWeight(750).toAttrs()) { Text(title) }
            P(OpsProseStyle.toModifier().margin(top = Space.SM, bottom = 0.px).toAttrs()) { Text(description) }
        }
        onRefresh?.let { refresh ->
            LButton(if (isRefreshing) "Refreshing…" else "Refresh", refresh,
                modifier = Modifier.styleModifier { property("min-width", "112px") },
                variant = LButtonVariant.SECONDARY, size = LButtonSize.SM,
                enabled = !isRefreshing, accessibleLabel = if (isRefreshing) "Refreshing $title" else "Refresh $title")
        }
    }
}

@Composable
fun OpsPanel(
    title: String,
    note: String? = null,
    actions: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    LCard(Modifier.width(100.percent)) {
        Column(Modifier.width(100.percent).gap(Space.MD)) {
            Div(OpsPanelHeaderStyle.toModifier().toAttrs()) {
                H2(
                    Modifier
                        .margin(0.px)
                        .fontSize(TypeScale.HeadingSmall)
                        .fontWeight(700)
                        .toAttrs(),
                ) { Text(title) }
                if (actions != null) {
                    Div { actions() }
                }
            }
            if (note != null) P(OpsProseStyle.toModifier().margin(0.px).toAttrs()) { Text(note) }
            content()
        }
    }
}

/** The only console composable that accepts customer-controlled text. It is deliberately a span. */
@Composable
fun OpsUntrustedText(value: Untrusted?, absent: String = "—") {
    Span { Text(value?.text?.takeIf(String::isNotBlank) ?: absent) }
}

@Composable
fun OpsFact(label: String, value: String?, absent: String = "—") {
    Column(Modifier.gap(Space.XXS).styleModifier { property("min-width", "0") }) {
        Div(
            Modifier
                .color(com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().textMuted)
                .fontSize(TypeScale.Micro)
                .fontWeight(600)
                .toAttrs(),
        ) { Text(label.uppercase()) }
        Div(Modifier.color(com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().textPrimary)
            .styleModifier { property("font-variant-numeric", "tabular-nums"); property("overflow-wrap", "anywhere") }.toAttrs()) {
            Text(value?.takeIf(String::isNotBlank) ?: absent)
        }
    }
}

sealed interface OpsLoad<out T> {
    data object Loading : OpsLoad<Nothing>
    data class Failed(val failure: OpsApiException) : OpsLoad<Nothing>
    data class OfflineStale<T>(val value: T, val checkedAt: String? = null) : OpsLoad<T>
    data class Ready<T>(val value: T) : OpsLoad<T>
}

/** Turns browser/network failures into the same retryable offline state as an HTTP refusal. */
fun opsFailure(cause: Throwable): OpsApiException = cause as? OpsApiException
    ?: OpsApiException(0, cause.message?.takeIf(String::isNotBlank) ?: "The operator API is unreachable.")

/** A completed empty read needs a sentence, not another bordered surface inside its panel. */
@Composable
fun OpsEmptyState(title: String, description: String) {
    Div(Modifier.width(100.percent).styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "8px")
        property("padding", "12px 0")
        property("min-width", "0")
    }.toAttrs { attr("role", "status") }) {
        P(Modifier.margin(0.px).fontSize(TypeScale.BodySmall).fontWeight(600).toAttrs()) { Text(title) }
        P(OpsProseStyle.toModifier().margin(0.px).toAttrs()) { Text(description) }
    }
}

@Composable
fun <T> OpsStates(
    state: OpsLoad<T>,
    empty: String,
    emptyDetail: String,
    filtered: String? = null,
    retry: () -> Unit,
    loaded: @Composable (T) -> Unit,
) {
    when (state) {
        OpsLoad.Loading -> Column(Modifier.gap(Space.SM)) {
            P(OpsProseStyle.toModifier().margin(0.px).toAttrs { attr("role", "status") }) { Text("Loading this panel…") }
            LSkeleton(height = 20.px, shimmer = false)
            LSkeleton(height = 96.px, shimmer = false)
        }
        is OpsLoad.Failed -> {
            val permission = state.failure.status in listOf(401, 403, 404)
            LAlert(
                title = when (state.failure.status) {
                    401 -> "Your session has expired"
                    403, 404 -> "This record is unavailable"
                    else -> "Could not load this panel"
                },
                description = if (permission) "The record could not be loaded for this session. Check the link or sign in again."
                    else state.failure.message,
                variant = if (permission) LAlertVariant.WARNING else LAlertVariant.DANGER,
                action = {
                    LButton("Retry", retry, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM)
                },
            )
        }
        is OpsLoad.OfflineStale -> {
            LAlert(
                title = "Offline — showing the last known result",
                description = state.checkedAt?.let { "Last checked ${formatUtc(it)}." }
                    ?: "The API is unreachable. Treat this result as stale.",
                variant = LAlertVariant.WARNING,
                action = { LButton("Retry", retry, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM) },
            )
            loaded(state.value)
        }
        is OpsLoad.Ready -> {
            if (isEmptyValue(state.value)) {
                OpsEmptyState(
                    title = if (filtered != null) filtered else empty,
                    description = if (filtered != null) "Try clearing one filter, or broaden the search."
                    else emptyDetail,
                )
            } else {
                loaded(state.value)
            }
        }
    }
}

/** Background refresh keeps only the current resource's data and labels its freshness explicitly. */
@Composable
fun <T> OpsResourceStates(
    state: OpsResourceState<T>,
    empty: String,
    emptyDetail: String,
    filtered: String? = null,
    retry: () -> Unit,
    loaded: @Composable (T) -> Unit,
) {
    if (state.isRefreshing) {
        P(OpsProseStyle.toModifier().margin(0.px).toAttrs { attr("role", "status") }) {
            Text("Refreshing… Showing the last loaded result until the check finishes.")
        }
    }
    if (state.refreshFailure != null) {
        LAlert("Could not refresh this panel", "Showing the last loaded result. Check your connection and retry.",
            variant = LAlertVariant.WARNING, live = true,
            action = { LButton("Retry refresh", retry, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM) })
    }
    Div(Modifier.width(100.percent).styleModifier {
        property("display", "flex"); property("flex-direction", "column"); property("gap", "16px"); property("min-width", "0")
    }.toAttrs { attr("aria-busy", (state.isRefreshing || state.load == OpsLoad.Loading).toString()) }) {
        OpsStates(state.load, empty, emptyDetail, filtered, retry, loaded)
    }
}

private fun isEmptyValue(value: Any?): Boolean = when (value) {
    is Collection<*> -> value.isEmpty()
    is OpsPage<*> -> value.rows.isEmpty()
    is OpsWindow<*> -> value.rows.isEmpty()
    else -> false
}

@Composable
fun OpsCapNotice(hitCap: Boolean, cappedAt: Int, unit: String) {
    if (hitCap) {
        LBadge(
            "Showing the first ${formatCount(cappedAt)} of more $unit. The number is the finding; " +
                "page two would not add to it.",
            variant = LBadgeVariant.WARNING,
        )
    }
}
